"use client";

import { useRef } from "react";

import { usePathname, useRouter } from "@/i18n/navigation";
import { AnimatedDragonLogo } from "@/shared/ui/animated-dragon-logo";

/** Сколько кликов подряд запускают пасхалку со схлопыванием монеты. */
const VANISH_AFTER_CLICKS = 7;

/**
 * Дракон в хедере — тот же логотип, что в hero-секции на главной
 * (AnimatedDragonLogo). Клик крутит всю «монету» (круг + дракон) на 360°
 * вокруг случайной 3D-оси в случайном направлении; вне главной после оборота
 * происходит переход на «/» (сама навигация по ссылке гасится, чтобы не
 * обрывать анимацию). Больше 7 кликов подряд — пасхалка: монета, вращаясь,
 * уменьшается до исчезновения и затем вырастает обратно на том же месте.
 * Родитель должен задать perspective.
 */
export function DragonLogo({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const clicks = useRef(0);
  const vanishing = useRef(false);
  const router = useRouter();
  const pathname = usePathname();

  const goHomeIfAway = () => {
    if (pathname !== "/") router.push("/");
  };

  const spin = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = ref.current;
    if (!el || vanishing.current) return;

    clicks.current += 1;

    if (clicks.current > VANISH_AFTER_CLICKS) {
      clicks.current = 0;
      vanishing.current = true;
      const shrink = el.animate(
        [
          { transform: "scale(1) rotate3d(0, 1, 0.2, 0deg)", opacity: 1 },
          { transform: "scale(0) rotate3d(0, 1, 0.2, 720deg)", opacity: 0 },
        ],
        { duration: 750, easing: "ease-in", fill: "forwards" },
      );
      shrink.onfinish = () => {
        window.setTimeout(() => {
          const grow = el.animate(
            [
              { transform: "scale(0)", opacity: 0 },
              { transform: "scale(1)", opacity: 1 },
            ],
            { duration: 650, easing: "cubic-bezier(0.34, 1.45, 0.64, 1)", fill: "forwards" },
          );
          grow.onfinish = () => {
            el.getAnimations().forEach((a) => a.cancel());
            vanishing.current = false;
          };
        }, 350);
      };
      return;
    }

    let x = Math.random() * 2 - 1;
    let y = Math.random() * 2 - 1;
    const z = (Math.random() * 2 - 1) * 0.35;
    // Вырожденная ось (почти нулевая) — фолбэк на «монетное» вращение по Y.
    if (Math.abs(x) + Math.abs(y) < 0.2) {
      x = 0;
      y = 1;
    }
    const dir = Math.random() < 0.5 ? -360 : 360;
    const turn = el.animate(
      [
        { transform: `rotate3d(${x}, ${y}, ${z}, 0deg)` },
        { transform: `rotate3d(${x}, ${y}, ${z}, ${dir}deg)` },
      ],
      { duration: 850, easing: "cubic-bezier(0.34, 1.2, 0.64, 1)" },
    );
    turn.onfinish = goHomeIfAway;
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- декор внутри <Link>: клавиатурой активируется сама ссылка
    <div
      ref={ref}
      onClick={spin}
      className={className ?? "h-full w-full"}
      style={{ transformStyle: "preserve-3d" }}
    >
      <AnimatedDragonLogo className="h-full w-full" />
    </div>
  );
}
